import { AlertTriangle, Building2, LogIn, Shield, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { PinPad } from "@/components/PinPad.tsx";
import { postLoginPath } from "@/constants/roles.ts";
import { API_BASE_URL, ApiError } from "@/lib/api.ts";
import { IS_ONLINE_EDITION } from "@/lib/edition.ts";
import { SESSION_EXPIRED_KEY } from "@/lib/setup-api.ts";
import { cn } from "@/lib/utils.ts";
import { useCashiers, useLoginPassword, useLoginPin } from "@/queries/auth.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useCompanyStore } from "@/stores/company.ts";
import type { CashierOut } from "@/types/api.ts";

type Tab = "cashier" | "admin";

export function LoginPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>("cashier");
  const companyRuc = useCompanyStore((s) => s.ruc);
  const clearCompany = useCompanyStore((s) => s.clear);
  const needsCompany = IS_ONLINE_EDITION && companyRuc === null;
  // Lee el flag al montar y lo limpia. Se setea desde setup-api cuando un 401
  // del backend forzó cierre de sesión.
  const [sessionExpired] = useState(
    () => sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1",
  );
  useEffect(() => {
    if (sessionExpired) sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  }, [sessionExpired]);

  // Si ya hay token + user, redirigir según rol.
  useEffect(() => {
    if (token && user) navigate(postLoginPath(user.role), { replace: true });
  }, [token, user, navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-slate-100 to-slate-200">
      <header className="border-b border-slate-200 bg-white/80 px-6 py-4 backdrop-blur">
        <h1 className="text-lg font-semibold text-slate-800">Sistema Sale POS</h1>
        <p className="text-sm text-slate-500">Iniciar sesión</p>
      </header>

      <main className="flex flex-1 flex-col items-center px-6 py-10">
        <div className="w-full max-w-2xl">
          {sessionExpired && (
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <p>Tu sesión expiró. Iniciá sesión de nuevo para continuar.</p>
            </div>
          )}

          {needsCompany ? (
            <CompanyPanel />
          ) : (
            <>
              {IS_ONLINE_EDITION && (
                <div className="mb-4 flex items-center justify-center gap-2 text-sm text-slate-600">
                  <Building2 className="h-4 w-4" />
                  <span>
                    Empresa RUC <strong>{companyRuc}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={clearCompany}
                    className="font-medium text-slate-900 underline-offset-2 hover:underline"
                  >
                    Cambiar
                  </button>
                </div>
              )}

              <div className="mb-6 flex justify-center gap-2 rounded-xl bg-white p-1 shadow-sm">
                <TabButton active={tab === "cashier"} onClick={() => setTab("cashier")}>
                  Cajero
                </TabButton>
                <TabButton active={tab === "admin"} onClick={() => setTab("admin")}>
                  Admin
                </TabButton>
              </div>

              {tab === "cashier" && <CashierLoginPanel />}
              {tab === "admin" && <AdminLoginPanel />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex-1 rounded-lg px-4 py-2 text-sm font-medium transition",
        active
          ? "bg-slate-900 text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-100",
      )}
    >
      {children}
    </button>
  );
}

// ---------- Company panel (edición online) ----------------------------------

function CompanyPanel() {
  const setRuc = useCompanyStore((s) => s.setRuc);
  const [value, setValue] = useState("");
  const ruc = value.replace(/\D/g, "");
  const isValid = ruc.length === 11;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isValid) setRuc(ruc);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-sm"
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-xl bg-slate-100 p-3 text-slate-700">
          <Building2 className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Empresa</h2>
          <p className="text-sm text-slate-500">Ingresa el RUC de tu empresa una sola vez.</p>
        </div>
      </div>

      <label htmlFor="company-ruc" className="mb-2 block text-sm font-medium text-slate-700">
        RUC
      </label>
      <input
        id="company-ruc"
        inputMode="numeric"
        autoComplete="off"
        maxLength={11}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-lg tracking-widest focus:border-slate-900 focus:outline-none"
        placeholder="20XXXXXXXXX"
        autoFocus
      />

      <button
        type="submit"
        disabled={!isValid}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-3 font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Continuar
      </button>
    </form>
  );
}

// ---------- Cashier panel (PIN flow) ---------------------------------------

function CashierLoginPanel() {
  const navigate = useNavigate();
  const [selectedCashier, setSelectedCashier] = useState<CashierOut | null>(null);
  const [pin, setPin] = useState("");

  const cashiers = useCashiers();
  const loginPin = useLoginPin();

  useEffect(() => {
    if (selectedCashier && pin.length === 4 && !loginPin.isPending) {
      loginPin.mutate(
        { user_id: selectedCashier.id, pin },
        {
          onSuccess: ({ user }) => {
            navigate(postLoginPath(user.role), { replace: true });
          },
          onError: () => {
            setPin("");
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, selectedCashier]);

  const closeModal = () => {
    setSelectedCashier(null);
    setPin("");
    loginPin.reset();
  };

  return (
    <>
      <h2 className="mb-6 text-center text-xl font-medium text-slate-700">
        Selecciona tu cajero
      </h2>

      {cashiers.isLoading && <p className="text-center text-slate-500">Cargando cajeros…</p>}
      {cashiers.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {IS_ONLINE_EDITION
            ? "No se pudo cargar la lista de cajeros. Verifica el RUC de la empresa y tu conexión a internet."
            : "No se pudo cargar la lista de cajeros. Verifica que el server local esté arriba en"}{" "}
          {!IS_ONLINE_EDITION && <code>{API_BASE_URL}</code>}
        </div>
      )}
      {cashiers.data && cashiers.data.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          No hay cajeros configurados. Pide al admin del tenant que cree usuarios con rol
          "cashier" y PIN.
        </div>
      )}
      {cashiers.data && cashiers.data.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {cashiers.data.map((c) => (
            <CashierCard key={c.id} cashier={c} onSelect={setSelectedCashier} />
          ))}
        </div>
      )}

      {selectedCashier && (
        <PinModal
          cashier={selectedCashier}
          pin={pin}
          onPinChange={setPin}
          onClose={closeModal}
          isPending={loginPin.isPending}
          error={
            loginPin.error instanceof ApiError ? loginPin.error.message : loginPin.error?.message
          }
        />
      )}
    </>
  );
}

function CashierCard({
  cashier,
  onSelect,
}: {
  cashier: CashierOut;
  onSelect: (c: CashierOut) => void;
}) {
  const initials = cashier.full_name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <button
      type="button"
      onClick={() => onSelect(cashier)}
      className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-slate-300 hover:shadow active:scale-[0.98]"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-900 text-xl font-semibold text-white">
        {initials || "?"}
      </div>
      <span className="text-center text-sm font-medium text-slate-800">{cashier.full_name}</span>
    </button>
  );
}

function PinModal({
  cashier,
  pin,
  onPinChange,
  onClose,
  isPending,
  error,
}: {
  cashier: CashierOut;
  pin: string;
  onPinChange: (p: string) => void;
  onClose: () => void;
  isPending: boolean;
  error: string | undefined;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">Iniciando sesión como</p>
            <h3 className="text-lg font-semibold text-slate-900">{cashier.full_name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 text-center text-sm text-slate-600">Ingresa tu PIN de 4 dígitos</p>

        <PinPad pin={pin} onPinChange={onPinChange} disabled={isPending} error={error} />

        {isPending && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-600">
            <LogIn className="h-4 w-4 animate-pulse" /> Validando…
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Admin panel (username + password) ------------------------------

function AdminLoginPanel() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const loginPassword = useLoginPassword();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    loginPassword.mutate(
      { username, password },
      {
        onSuccess: ({ user }) => {
          navigate(postLoginPath(user.role), { replace: true });
        },
      },
    );
  };

  const errorMsg =
    loginPassword.error instanceof ApiError
      ? loginPassword.error.message
      : loginPassword.error?.message;

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-sm"
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-xl bg-slate-100 p-3 text-slate-700">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Acceso administrativo</h2>
          <p className="text-sm text-slate-500">Catálogo, sucursales, usuarios.</p>
        </div>
      </div>

      <label className="mb-2 mt-4 block text-sm font-medium text-slate-700">
        Usuario
      </label>
      <input
        type="text"
        autoComplete="username"
        autoFocus
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-base outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
        placeholder="admin"
      />

      <label className="mb-2 mt-4 block text-sm font-medium text-slate-700">
        Contraseña
      </label>
      <input
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-base outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
        placeholder="••••••••"
      />

      {errorMsg && (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {errorMsg}
        </p>
      )}

      <button
        type="submit"
        disabled={loginPassword.isPending || !username || !password}
        className="mt-6 w-full rounded-xl bg-slate-900 py-3 text-base font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
      >
        {loginPassword.isPending ? "Validando…" : "Ingresar"}
      </button>
    </form>
  );
}
